import { css } from '@emotion/core'
import { PagedTable } from 'bold-ui'
import { IconLabel } from 'components/IconLabel'
import { usePagination } from 'components/table/usePagination'
import { useAcompanhamentoPreNatalVisitasDomiciliaresQuery, useCidadaoQuery } from 'graphql/hooks.generated'
import { VisitaDomiciliarPreNatal } from 'graphql/types.generated'
import React from 'react'
import { formatDate } from 'util/date/formatDate'
import { DetalhesUltimosContatosButton } from 'view/atendimentos/detail/folha-rosto/components/ultimos-contatos/detalhes/DetalhesUltimosContatosButton'
import { ProfissionalListLabel } from 'view/atendimentos/detail/historico/components/accordion/detail/ProfissionalListLabel'

import { AcompanhamentoCard } from '../../components/AcompanhamentoCard'
import { AcompanhamentoHeaderLink } from '../../components/AcompanhamentoHeaderLink'
import { convertFatoVisitaDomiciliarToContatosModel } from '../converter-acompanhamentoPreNatal'
import { formatSemanas } from '../formatter-acompanhamentoPreNatal'
import { statusVisitaPreNatalRecord } from '../model-acompanhamentoPreNatal'

interface VisitasDomiciliaresGestacaoCardProps {
  cidadaoId: ID
  dataInicioGestacao: Date
  historicoPath: string
}

const renderStatus = (visita: VisitaDomiciliarPreNatal) => {
  const status = statusVisitaPreNatalRecord[visita.visitaDomiciliar.dimensaoDesfechoVisita.descricao]
  return <IconLabel icon={status.icon} text={status.label} color={status.color} hSpacing={0.5} />
}

const renderProfissional = (visita: VisitaDomiciliarPreNatal) => (
  <ProfissionalListLabel
    nome={visita.visitaDomiciliar.dimensaoProfissional.nome}
    cbo={visita.visitaDomiciliar.dimensaoCbo.nome}
    nomeEquipe={visita.visitaDomiciliar.dimensaoEquipe.nome}
    ineEquipe={visita.visitaDomiciliar.dimensaoEquipe.ine}
  />
)

export function VisitasDomiciliaresGestacaoCard(props: Readonly<VisitasDomiciliaresGestacaoCardProps>) {
  const { cidadaoId, dataInicioGestacao, historicoPath } = props

  const { data: cidadaoData, loading: loadingCidadao } = useCidadaoQuery({
    fetchPolicy: 'cache-first',
    variables: { id: cidadaoId },
    skip: !cidadaoId,
  })

  const { data, loading } = useAcompanhamentoPreNatalVisitasDomiciliaresQuery({
    variables: {
      input: { cidadaoId, dataInicioGestacao },
    },
    skip: !cidadaoId || !dataInicioGestacao,
  })

  const { paginatedItems, tableProps } = usePagination({
    items: data?.acompanhamentoPreNatalVisitasDomiciliares || [],
    initialPageSize: 8,
    sizeOptions: [8],
  })

  const hasVisitas = data?.acompanhamentoPreNatalVisitasDomiciliares?.length > 0

  const renderVisualizarHistorico = (visita: VisitaDomiciliarPreNatal) =>
    visita.visitaDomiciliar.dimensaoDesfechoVisita.descricao === 'Visita realizada' && (
      <DetalhesUltimosContatosButton
        contato={convertFatoVisitaDomiciliarToContatosModel(visita.visitaDomiciliar)}
        dataNascimentoCidadao={cidadaoData?.cidadao?.dataNascimento}
      />
    )

  return (
    <AcompanhamentoCard
      id='visitas-domiciliares'
      title='Visitas domiciliares'
      loading={loading || loadingCidadao}
      placeholder='Nenhum registro encontrado'
      headerContent={<AcompanhamentoHeaderLink title='Visualizar histórico' path={historicoPath} />}
      justifyHeaderContent='space-between'
      bodyPadding={hasVisitas ? '0' : undefined}
    >
      {hasVisitas && (
        <PagedTable
          rows={paginatedItems || []}
          size={tableProps.pageSize}
          columns={[
            {
              name: 'dataVisita',
              header: 'Data',
              render: (visita: VisitaDomiciliarPreNatal) => formatDate(visita.visitaDomiciliar.dataVisita),
            },
            {
              name: 'status',
              header: 'Status',
              render: renderStatus,
            },
            {
              name: 'profissional',
              header: 'Profissional',
              render: renderProfissional,
            },
            {
              name: 'igVisita',
              header: 'IG na visita',
              render: (visita: VisitaDomiciliarPreNatal) => formatSemanas(visita.idadeGestacionalEmSemanas),
            },
            {
              name: 'visualizarHistorico',
              header: '',
              style: styles.lupa,
              render: renderVisualizarHistorico,
            },
          ]}
          {...tableProps}
        />
      )}
    </AcompanhamentoCard>
  )
}

const styles = {
  lupa: css`
    padding: 0;
  `,
}
